<template>
    <v-container fluid>
        <v-row>
            <v-col>
                <loading-spinner v-if="isLoading"></loading-spinner>
                <v-card v-else>
                    <card-header :title="$t('title.journal_detail')" icon="menu" color="primary"
                        variant="gradient"></card-header>

                    <v-card-text>
                        <v-row>
                            <v-col class="d-flex justify-center align-center">
                                <v-card width="200" height="250"
                                    class="image-upload-card d-flex justify-center align-center">
                                    <template v-if="journal.image_path">
                                        <v-img 
                                            :src="imageUrl"
                                            max-width="200" 
                                            max-height="250" 
                                            contain
                                        >
                                            <template v-slot:placeholder>
                                                <v-row class="fill-height ma-0" align="center" justify="center">
                                                    <v-progress-circular indeterminate></v-progress-circular>
                                                </v-row>
                                            </template>
                                        </v-img>
                                    </template>
                                    <template v-else>
                                        <div class="text-center">
                                            <v-icon size="64" color="grey lighten-1">mdi-image</v-icon>
                                        </div>
                                    </template>
                                </v-card>
                            </v-col>
                        </v-row>

                        <v-row>
                            <v-col cols="12" xs="12" sm="6" md="6" lg="6" xl="6">
                                <v-text-field :value="journal.journal_type?.name" :label="$t('label.journal_category')"
                                    prepend-icon="book" readonly></v-text-field>
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-col cols="12" xs="12" sm="6" md="6" lg="6" xl="6">
                                <v-text-field v-model="journal.title" :label="$t('label.journal_title')"
                                    prepend-icon="title" readonly></v-text-field>
                            </v-col>
                            <v-col cols="12" xs="12" sm="6" md="6" lg="6" xl="6">
                                <v-text-field v-model="journal.title_eng" :label="$t('label.journal_title_eng')"
                                    prepend-icon="title" readonly></v-text-field>
                            </v-col>
                        </v-row>

                        <v-row v-show="isJournalType">
                            <v-col cols="12" xs="12" sm="6" md="6" lg="6" xl="6">
                                <v-text-field v-model="journal.issn" :label="$t('label.journal_issn')"
                                    prepend-icon="notes" readonly></v-text-field>
                            </v-col>
                            <v-col cols="12" xs="12" sm="6" md="6" lg="6" xl="6">
                                <v-text-field v-model="journal.eissn" :label="$t('label.journal_eissn')"
                                    prepend-icon="notes" readonly></v-text-field>
                            </v-col>
                        </v-row>

                        <v-row>
                            <v-col cols="12" xs="12" sm="6" md="6" lg="6" xl="6">
                                <v-text-field v-model="journal.submission_start_date"
                                    :label="$t('label.journal_submission_start_date')" prepend-icon="event"
                                    readonly></v-text-field>
                            </v-col>

                            <v-col cols="12" xs="12" sm="6" md="6" lg="6" xl="6">
                                <v-text-field v-model="journal.submission_end_date"
                                    :label="$t('label.journal_submission_end_date')" prepend-icon="event"
                                    readonly></v-text-field>
                            </v-col>
                        </v-row>

                        <v-row>
                            <v-col cols="12" xs="12" sm="6" md="6" lg="6" xl="6">
                                <v-text-field v-model="researchEthicsStatement"
                                    :label="$t('label.research_ethics_statement')" prepend-icon="notes"
                                    readonly></v-text-field>
                            </v-col>
                            <v-col cols="12" xs="12" sm="6" md="6" lg="6" xl="6">
                                <v-text-field v-model="copyrightTransferAgreement"
                                    :label="$t('label.copyright_transfer_agreement')" prepend-icon="notes"
                                    readonly></v-text-field>
                            </v-col>
                        </v-row>

                        <v-row>
                            <v-col cols="12" xs="12" sm="6" md="6" lg="6" xl="6">
                                <v-text-field v-model="plagiarismCheckAgreement"
                                    :label="$t('label.plagiarism_check_agreement')" prepend-icon="notes"
                                    readonly></v-text-field>
                            </v-col>

                            <v-col cols="12" xs="12" sm="6" md="6" lg="6" xl="6">
                                <v-text-field v-model="journal.publish_date" :label="$t('label.journal_publish_date')"
                                    prepend-icon="event" readonly></v-text-field>
                            </v-col>
                        </v-row>

                        <v-row>
                            <v-col cols="12" xs="12" sm="6" md="6" lg="6" xl="6">
                                <v-text-field 
                                    v-model="journal.year" 
                                    :label="$t('label.journal_year')"
                                    type="number"
                                    prepend-icon="notes"
                                    readonly
                                ></v-text-field>
                            </v-col>
                            <v-col cols="12" xs="12" sm="6" md="6" lg="6" xl="6">
                                <v-text-field 
                                    v-model="journal.volume" 
                                    :label="$t('label.journal_volume')"
                                    type="number"
                                    prepend-icon="notes"
                                    readonly
                                ></v-text-field>
                            </v-col>
                            <v-col cols="12" xs="12" sm="6" md="6" lg="6" xl="6">
                                <v-text-field 
                                    v-model="journal.issue" 
                                    :label="$t('label.journal_issue')"
                                    type="number"
                                    prepend-icon="notes"
                                    readonly
                                ></v-text-field>
                            </v-col>
                            <v-col cols="12" xs="12" sm="6" md="6" lg="6" xl="6" v-if="isJournalType">
                                <v-text-field 
                                    v-model="journal.doi_suffix" 
                                    :label="$t('label.journal_doi_suffix')"
                                    prepend-icon="notes"
                                    readonly
                                ></v-text-field>
                            </v-col>
                        </v-row>

                        <v-row>
                            <v-col cols="12">
                                <v-textarea v-model="journal.description" :label="$t('label.journal_description')"
                                    outlined dense readonly></v-textarea>
                            </v-col>
                        </v-row>
                    </v-card-text>

                    <v-card-actions class="d-flex align-center justify-end">
                        <custom-button @click="toJournalManagement" :label="$t('button.cancel')" color="black" size="lg"
                            gradient rounded></custom-button>
                    </v-card-actions>
                </v-card>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import CardHeader from '@/components/cards/CardHeader.vue';
import CustomButton from '@/components/common/buttons/CustomButton.vue';
import LoadingSpinner from '@/components/common/Spinners/LoadingSpinner.vue';

import { journalApi, fileApi } from '@/api/api';

export default {
    components: {
        CardHeader,
        CustomButton,
        LoadingSpinner,
    },
    props: {
        idx: {
            type: [Number, String],
            required: true,
        },
    },
    data() {
        return {
            journal: {
                title: '',
                title_eng: '',
                journal_type_idx: '',
                image_path: null,
                description: '',
                issn: null,
                eissn: null,
                year: null,
                volume: null,
                issue: null,
                doi_suffix: null,
                research_ethics_statement: null,
                copyright_transfer_agreement: null,
                plagiarism_check_agreement: null,
                submission_start_date: '',
                submission_end_date: '',
                publish_date: '',
            },
            isLoading: false,
            imageBlob: null,
        }
    },
    watch: {
        'journal.image_path': {
            immediate: true,
            handler(newPath) {
                if (newPath) {
                    this.loadImage();
                } else {
                    if (this.imageBlob) {
                        URL.revokeObjectURL(this.imageBlob);
                        this.imageBlob = null;
                    }
                }
            }
        },
    },
    methods: {
        async toJournalManagement() {
            this.$router.push({ name: "journals-management" });
        },
        /* **********************************************************************************
        * API 호출
        * ********************************************************************************** */
        async fetchJournal() {
            try {
                this.isLoading = true;
                const params = {
                    idx: this.idx
                }
                const response = await journalApi.showJournal(params);
                if (response.status === 200) {
                    this.journal = response.data.response;
                }
            } catch (error) {
                this.notifyVue({ message: this.$t('alert.failed_request'), style: "danger" });
                this.$router.push({ name: "journals-management" });
            } finally {
                this.isLoading = false;
            }
        },
        async loadImage() {
            try {
                const response = await fileApi.fileDownload({ 
                    // down_file_name: this.journal.image_path.split('/').pop(),
                    file_name: this.journal.image_path
                });
                if (this.imageBlob) {
                    URL.revokeObjectURL(this.imageBlob);
                }
                this.imageBlob = URL.createObjectURL(response.data);
            } catch (error) {
                this.notifyVue({ message: this.$t('alert.failed_fetch_image'), style: "danger" });
            }
        },
        /* ****************************************************************************************
         * 알럿
         * ****************************************************************************************/
        notifyVue({
            vertical = "top",
            horizontal = "right",
            message = "",
            style = "primary",
            icon = "priority_high",
        } = {}) {
            this.$notify({
                timeout: 8000,
                message: message,
                icon: icon,
                type: style,
                horizontalAlign: horizontal,
                verticalAlign: vertical,
            });
        }
    },
    computed: {
        researchEthicsStatement() {
            return this.journal.consent_documents?.find(
                doc => doc.document_type_code === 'research_ethics_statement'
            )?.title || '';
        },
        copyrightTransferAgreement() {
            return this.journal.consent_documents?.find(
                doc => doc.document_type_code === 'copyright_transfer_agreement'
            )?.title || '';
        },
        plagiarismCheckAgreement() {
            return this.journal.consent_documents?.find(
                doc => doc.document_type_code === 'plagiarism_check_agreement'
            )?.title || '';
        },
        imageUrl() {
            return this.imageBlob;
        },
        isJournalType() {
            return this.journal.journal_type?.code === 'jnl';
        }
    },
    mounted() {
        this.fetchJournal();
    },
    beforeDestroy() {
        if (this.imageBlob) {
            URL.revokeObjectURL(this.imageBlob);
        }
    }
}
</script>


<style scoped>
.image-upload-card {
    border: 2px dashed #e0e0e0;
    overflow: hidden;
}
</style>